// @fileoverview : 경남-KPI > 설비가동률(P)
import { KPIActionHeader } from "@/features/kpi/actionHeader";
import { useKPIData } from "@/features/kpi/hook/useKPIData";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { GridTable } from "@/features/ui/PMS/gridTable";
import CustomTable from "@/features/ui/table/CustomTable";
import useColumnList from "@/hooks/useColumnList";
import { Audit } from "@/pages/KPI/leadtime";
import { KPIPageProps } from "@/pages/KPI/type";
import { DatetimeUtilMinute } from "@/utils/dateTimeUtil";
import { Flex } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const KPIRateOfOperation = Object.assign(
  {},
  {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    KPIActionHeader: KPIActionHeader,
    CustomTable: CustomTable,
  },
);

export const KPIRateOfOperationPage = ({ mode }: KPIPageProps) => {
  const queryClient = useQueryClient();
  const today = dayjs().subtract(1, "day").startOf("day").toDate();
  const oneWeekAgo = dayjs().subtract(7, "day").startOf("day").toDate();
  const columnlist = useColumnList();
  const { t } = useTranslation();

  const [content, setContent] = useState<any>([]);
  const [selectList, setSelectList] = useState<Set<number>>(new Set());
  const [stateDate, setStateDate] = useState<[Date | null, Date | null]>([oneWeekAgo, today]);
  const [equipment, setEquipment] = useState<string | null>("");
  const [averageRateOfOperation, setAverageRateOfOperation] = useState<string>("");

  const { equipmentData, rateOfOperationData, auditKPIData } = useKPIData({
    mode,
    value: equipment,
    stateDate,
    options: Audit ? { fetchAuditKPI: true } : { fetchRateOfOperation: true },
  });

  const TableHeader = {
    machine: [t("equipment.name"), t("kpi.rateOperation.averageRateEquipment")],
  };

  const BodyData = {
    machine: [
      {
        [t("equipment.name")]: equipmentData?.name ?? "-",
        [t("kpi.rateOperation.averageRateEquipment")]:
          averageRateOfOperation === "NaN" ? "-" : averageRateOfOperation,
      },
    ],
  };

  useEffect(() => {
    const transfrom = (data: any[]) =>
      data.map((d) => ({
        id: d.id,
        productionPlanId: d.productionPlanId,
        operationName: d.operationName,
        equipmentName: d.equipmentName,
        itemCode: d.itemCode,
        itemName: d.itemName,
        itemSpec: d.itemSpec,
        itemStandardUph: d.uph.toFixed(2),
        workerName: d.workerName,
        workStartTime: d.workStartTime === null ? "-" : DatetimeUtilMinute(d.workStartTime),
        workEndTime: d.workEndTime === null ? "-" : DatetimeUtilMinute(d.workEndTime),
        pauseDuration: d.pauseDuration,
        totalQuantity: d.totalQuantity,
        goodQuantity: d.goodQuantity,
        defectQuantity: d.defectQuantity,
        capacityUtilization: d.capacityUtilization,
      }));

    const calculateAverage = (data: any[]) => {
      if (!Array.isArray(data) || data.length === 0) return "0.00";
      const total = data.reduce((sum, d) => sum + parseFloat(d.capacityUtilization), 0);
      return (total / data.length).toFixed(2);
    };

    if (Array.isArray(rateOfOperationData?.data)) {
      const transformed = transfrom(rateOfOperationData?.data ?? []);
      setContent(transformed);
      setAverageRateOfOperation(calculateAverage(rateOfOperationData?.data ?? []));
    } else if (Array.isArray(auditKPIData?.data?.rows ?? [])) {
      const rows = auditKPIData?.data.rows;
      const transformed = transfrom(rows ?? []);
      setContent(transformed);
      setAverageRateOfOperation(calculateAverage(rows ?? []));
    } else {
      setContent([]);
      setAverageRateOfOperation("0.00");
    }
  }, [rateOfOperationData, auditKPIData, equipmentData]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["kpi"] });
    };
  }, []);

  return (
    <Flex gap="md" direction={"column"} p="sm">
      <KPIRateOfOperation.Header>
        <KPIRateOfOperation.HeaderTitle>
          {t("kpi.rateOperation.rateOperation")}(P)
        </KPIRateOfOperation.HeaderTitle>
        <KPIRateOfOperation.HeaderSubTitle>
          {t("kpi.rateOperation.subTitle")}
        </KPIRateOfOperation.HeaderSubTitle>
      </KPIRateOfOperation.Header>
      <KPIRateOfOperation.KPIActionHeader
        setItem={setEquipment}
        stateDate={stateDate}
        setStateDate={setStateDate}
        mode={mode}
      />

      <KPIRateOfOperation.CustomTable headers={TableHeader.machine} data={BodyData.machine} />
      <GridTable
        headerList={columnlist.rateOfoperationTable}
        row={content}
        setRow={setContent}
        selectList={selectList}
        //@ts-ignore
        setSelectList={setSelectList}
        width={"100%"}
        height={750}
        resizable={true}
      />
    </Flex>
  );
};
